import isEqual from "lodash/isEqual";
import cloneDeep from "lodash/cloneDeep";

import { putRequestCase, postRequestCase, deleteRequestCase } from "@/stores/caseStore/backendApi";
import { useCaseStore } from "@/stores/caseStore/0_caseStore";
import { withQuestionnaireSource } from "@/stores/caseStore/questionnaireSource";
import { useSnackbarStore } from "@/stores/snackbarStore";
import * as d from "@/utils/dataUtils";

// model

export const defaultClient = {
  id: "",

  clientType: null,
  company: "",
  salutation: null,
  title: null,
  firstName: "",
  lastName: "",
  birthdate: null,
  languages: null,

  streetNo: "",
  zip: "",
  city: "",
  country: null,
  mobile: "",
  landline: "",
  email: "",
  homepage: "",

  personDamage: null,
  personDamageToBeClaimed: null,
  personDamageTreated: null,
  personDamageInjuries: null,

  roleOwner: null,
  roleHolder: null,
  roleDriver: null,
  roleInsured: null,
  roleLeasing: null,

  legalExpenseInsuranceExists: null,
  legalExpenseInsuranceName: "",
  legalExpenseInsuranceDeductible: null,
  legalExpenseInsurancePolicyNumber: "",
  legalExpenseInsuranceCaseNumber: "",
  legalExpenseInsuranceCoverageAsked: null,
  legalExpenseInsuranceCoverage: null,

  iban: "",
  accountHolder: "",
  seizureProtectionAccount: null,

  remarks: "",

  // Abweichende Korrespondenzperson (Kanzlei Polz, 16.10.2026)
  correspondenceName: "",
  correspondenceRelation: "",
  correspondenceEmail: "",
  correspondencePhone: "",
  correspondenceReason: "",
};

// CRUD

/**
 * Quelle einer Kundendaten-Bearbeitung. "akte": Die Kanzlei bearbeitet in der
 * Akte (XXIV, 18.10.2026) – das Backend legt keine Fragebogen-Version an.
 */
export type ClientEditSource = "akte";

/**
 * Fehler werden per Snackbar gemeldet und – wie bisher – NICHT weitergereicht.
 * Mit `{ rethrow: true }` zusätzlich weiterreichen (Fragebogen-Hülle: zeigt
 * dann „Nicht gespeichert“ statt „Gespeichert“). `source` kennzeichnet den
 * Aufruf als Akten-Bearbeitung (?source=akte); ohne Angabe gilt der Fragebogen.
 */
export async function saveClients(options: { rethrow?: boolean; source?: ClientEditSource } = {}): Promise<void> {
  const store = useCaseStore();
  const snackbar = useSnackbarStore();

  try {
    const current = store.clients.map(prepareClientForSave);
    const previous = store.lastSavedClients.map(prepareClientForSave);

    if (isEqual(current, previous)) return;

    // XXIV (18.10.2026): Bearbeitung in der Akte als solche kennzeichnen –
    // keine Fragebogen-Version, Chronologie „Kundendaten bearbeitet von …".
    // 25.10.2026 (VIII): aus der Fragebogen-Sicht ?source=fragebogen.
    const query = options.source === "akte" ? "?source=akte" : "";
    for (const client of current) {
      await putRequestCase(withQuestionnaireSource(`/protected/cases/${store.caseData.id}/clients/${client.id}${query}`, store.questionnaireView), client);
    }

    store.lastSavedClients = cloneDeep(store.clients);
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : (error as any)?.message || "Fehler beim Speichern";
    snackbar.showSnackbar({ message: msg, type: "error" });
    if (options.rethrow) throw error;
  }
}

export async function addClient(): Promise<void> {
  const store = useCaseStore();
  const response = await postRequestCase(`/protected/cases/${store.caseData.id}/clients`);
  store.clients.push(prepareClientForLoad(response));
  store.lastSavedClients = cloneDeep(store.clients);
}

export async function deleteClient(idClient: string): Promise<void> {
  const store = useCaseStore();
  const index = store.clients.findIndex((v) => String(v.id) === String(idClient));
  if (index === -1) return;

  // Kundenfeedback 26.10.2026 (VIII): Löschen aus dem Fragebogen – ein
  // Eintrag mit Inhalt bleibt in der Akte, bis die Kanzlei entscheidet.
  await deleteRequestCase(withQuestionnaireSource(`/protected/cases/${store.caseData.id}/clients/${idClient}`, store.questionnaireView));
  store.clients.splice(index, 1);
  store.lastSavedClients = cloneDeep(store.clients);
}

// data helper
type Client = Record<string, any>;

export function prepareClientForLoad(data: Client): Client {
  return {
    ...data,

    clientType: d.loadSelect(data.clientType),
    salutation: d.loadSelect(data.salutation),
    title: d.loadSelect(data.title),
    birthdate: d.loadDate(data.birthdate),
    languages: d.loadArray(data.languages),

    country: d.loadSelect(data.country),
    // Telefon-Felder können im Backend null sein (nie befüllt) – für die
    // Frontend-Validierung (phoneRules) muss immer ein String vorliegen.
    mobile: data.mobile ?? "",
    landline: data.landline ?? "",

    personDamage: d.loadTristate(data.personDamage),
    personDamageToBeClaimed: d.loadTristate(data.personDamageToBeClaimed),
    personDamageTreated: d.loadTristate(data.personDamageTreated),
    personDamageInjuries: d.loadArray(data.personDamageInjuries),

    roleOwner: d.loadTristate(data.roleOwner),
    roleHolder: d.loadTristate(data.roleHolder),
    roleDriver: d.loadTristate(data.roleDriver),
    roleInsured: d.loadTristate(data.roleInsured),
    roleLeasing: d.loadTristate(data.roleLeasing),

    legalExpenseInsuranceExists: d.loadTristate(data.legalExpenseInsuranceExists),
    legalExpenseInsuranceDeductible: d.loadDecimal(data.legalExpenseInsuranceDeductible),
    legalExpenseInsuranceCoverageAsked: d.loadTristate(data.legalExpenseInsuranceCoverageAsked),
    legalExpenseInsuranceCoverage: d.loadTristate(data.legalExpenseInsuranceCoverage),

    seizureProtectionAccount: d.loadTristate(data.seizureProtectionAccount),
  };
}

export function prepareClientForSave(data: Client): Client {
  return {
    ...data,

    clientType: d.saveSelect(data.clientType),
    salutation: d.saveSelect(data.salutation),
    title: d.saveSelect(data.title),
    birthdate: d.saveDate(data.birthdate),

    languages: d.saveArray(data.languages),

    country: d.saveSelect(data.country),

    personDamage: d.saveTristate(data.personDamage),
    personDamageToBeClaimed: d.saveTristate(data.personDamageToBeClaimed),
    personDamageTreated: d.saveTristate(data.personDamageTreated),
    personDamageInjuries: d.saveArray(data.personDamageInjuries),

    roleOwner: d.saveTristate(data.roleOwner),
    roleHolder: d.saveTristate(data.roleHolder),
    roleDriver: d.saveTristate(data.roleDriver),
    roleInsured: d.saveTristate(data.roleInsured),
    roleLeasing: d.saveTristate(data.roleLeasing),

    legalExpenseInsuranceExists: d.saveTristate(data.legalExpenseInsuranceExists),
    legalExpenseInsuranceDeductible: d.saveFloat(data.legalExpenseInsuranceDeductible),
    legalExpenseInsuranceCoverageAsked: d.saveTristate(data.legalExpenseInsuranceCoverageAsked),
    legalExpenseInsuranceCoverage: d.saveTristate(data.legalExpenseInsuranceCoverage),

    seizureProtectionAccount: d.saveTristate(data.seizureProtectionAccount),
  };
}
